import { useState, useEffect, useCallback, useRef } from 'react';
import useUserId from './useUserId2';

const useFetchConversations = () => {
    const { userId } = useUserId();
    const [data, setData] = useState([]);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const [isError, setIsError] = useState(null);
    const PAGE_SIZE = 10;
    
    // Reference to track if we're currently fetching to prevent duplicate fetches
    const fetchingRef = useRef(false);
    // Reference to store if initial load has been done
    const initialLoadDoneRef = useRef(false);
    // Store the last fetched page to prevent duplicate fetches
    const lastFetchedPageRef = useRef(0);
    
    // Function to fetch conversations but only triggered manually
    const fetchConversations = useCallback(async (pageToFetch) => {
        // Don't fetch if already fetching or if this page was just fetched
        if (fetchingRef.current || pageToFetch === lastFetchedPageRef.current) {
            console.log(`Skipping fetch - already fetching or page ${pageToFetch} was just fetched`);
            return;
        }
        
        if (!userId) return;
        
        // Mark as fetching
        fetchingRef.current = true;
        lastFetchedPageRef.current = pageToFetch;
        
        const isInitialLoad = pageToFetch === 1;
        
        try {
            if (isInitialLoad) {
                setIsLoading(true);
            } else {
                setIsLoadingMore(true);
            }
            
            console.log(`Fetching page ${pageToFetch} of conversations for user ${userId}`);
            
            const apiUrl = process.env.REACT_APP_API_URL;
            const url = `${apiUrl}messaging/api/conversations/${userId}/?page=${pageToFetch}&page_size=${PAGE_SIZE}`;
            
            console.log('Request URL:', url);
            
            const response = await fetch(url, {
                headers: {
                    "Content-type": "application/json",
                    "Authorization": "Bearer " + localStorage.getItem("accessToken"),
                },
            });
            
            if (!response.ok) throw new Error('Failed to fetch conversations');
            
            const result = await response.json();
            
            if (result.status === "success") {
                const newConversations = result.conversations || [];
                
                // Add debug log for pagination info
                if (result.pagination) {
                    console.log('Pagination info:', {
                        page: result.pagination.page,
                        pageSize: result.pagination.pageSize,
                        total: result.pagination.total,
                        hasMore: result.pagination.hasMore
                    });
                }
                
                // If it's the first page, replace data, otherwise append
                if (isInitialLoad) {
                    setData(newConversations);
                } else {
                    // Check if we've received any new conversations
                    if (newConversations.length === 0) {
                        setHasMore(false);
                    } else {
                        setData(prev => {
                            // Create a map of existing conversation IDs for faster lookup
                            const existingIdsMap = new Map();
                            
                            // Store each existing conversation ID
                            prev.forEach(conv => {
                                if (conv.other_user && conv.other_user.id) {
                                    existingIdsMap.set(conv.other_user.id, true);
                                }
                            });
                            
                            // Filter out duplicates from new conversations
                            const uniqueNewConversations = newConversations.filter(conv => {
                                if (!conv.other_user || !conv.other_user.id) {
                                    console.warn('Found conversation without other_user or id:', conv);
                                    return false;
                                }
                                
                                const isNew = !existingIdsMap.has(conv.other_user.id);
                                if (!isNew) {
                                    console.log(`Filtered out duplicate conversation with user ${conv.other_user.id}`);
                                }
                                return isNew;
                            });
                            
                            console.log(`Adding ${uniqueNewConversations.length} new unique conversations (filtered ${newConversations.length - uniqueNewConversations.length} duplicates)`);
                            
                            // If all new conversations were duplicates but there should be more, 
                            // we need to fetch the next page immediately
                            if (uniqueNewConversations.length === 0 && newConversations.length > 0) {
                                console.log('All fetched conversations were duplicates, will fetch next page on next user action');
                            }
                            
                            return [...prev, ...uniqueNewConversations];
                        });
                    }
                }
                
                // Update hasMore flag based on pagination info
                if (result.pagination && typeof result.pagination.hasMore === 'boolean') {
                    setHasMore(result.pagination.hasMore);
                } else {
                    // If no pagination info or hasMore is not provided, infer from results
                    setHasMore(newConversations.length >= PAGE_SIZE);
                }
                
                if (isInitialLoad) {
                    initialLoadDoneRef.current = true;
                }
            } else {
                console.error("API returned error:", result);
                setHasMore(false);
            }
        } catch (err) {
            console.error('Error fetching conversations:', err);
            setIsError(err.message);
            setHasMore(false);
        } finally {
            if (isInitialLoad) {
                setIsLoading(false);
            } else {
                setIsLoadingMore(false);
            }
            // Release the fetching lock
            fetchingRef.current = false;
        }
    }, [userId, PAGE_SIZE]);

    // Load first page when userId changes - but only once
    useEffect(() => {
        if (userId && !initialLoadDoneRef.current) {
            console.log('Initial load of conversations triggered by userId change');
            setPage(1);
            setHasMore(true);
            setData([]);
            fetchConversations(1);
        }
    }, [userId, fetchConversations]);
    
    // DO NOT automatically trigger fetches when page changes
    // Instead, page changes are used by the loadMore function only
    
    // Function to manually trigger loading the next page
    const loadMore = useCallback(() => {
        if (hasMore && !isLoadingMore && !fetchingRef.current) {
            const nextPage = page + 1;
            console.log(`Manually loading more conversations, page ${nextPage}`);
            setPage(nextPage);
            fetchConversations(nextPage);
        } else {
            console.log("Cannot load more:", { hasMore, isLoadingMore, isFetching: fetchingRef.current });
        }
    }, [hasMore, isLoadingMore, page, fetchConversations]);

    // Function to manually refetch from the beginning
    const refetch = useCallback(() => {
        console.log('Manually refetching conversations from the beginning');
        setData([]);
        setPage(1);
        setHasMore(true);
        setIsError(null);
        initialLoadDoneRef.current = false;
        fetchConversations(1);
    }, [fetchConversations]);

    return { 
        data, 
        isLoading,
        isLoadingMore,
        isError, 
        refetch,
        hasMore,
        loadMore,
        page,
        pageSize: PAGE_SIZE
    };
};

export default useFetchConversations;